import { Link } from "react-router-dom";
import React, { useState, useEffect } from "react";
import { Input } from "../../components/ui/input";
import { PenIcon } from "lucide-react";
import ProfileCreationForm from "../../components/dashboard/ProfileCompletionForm";
import PieChart from "../../components/dashboard/PieChart";
import { getUserField, getUserDisplayInfo } from "../../utils/userNormalizer";
import { profileApi } from "../../api/profileApi.ts";
import { userDataFormat } from "@/utils/userDataFormat.ts";

const statChartColor = [
  {
    label: "Courses Completed",
    key: "courses",
    color: "#22c55e",
    bgColor: "#e8e8e8",
  },
  {
    label: "Quizzes Taken",
    key: "quizzes",
    color: "#f59e42",
    bgColor: "#e8e8e8",
  },
  {
    label: "Materials Completed",
    key: "materials",
    color: "#ef4444",
    bgColor: "#e8e8e8",
  },
];

export default function Profile() {
  const [showProfileForm, setShowProfileForm] = useState(false);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageError, setImageError] = useState(false);

  const refetchProfile = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await profileApi.fetchProfile();
      setProfile(userDataFormat(data));
    } catch (err) {
      setError(err.message || "Failed to load profile");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      await refetchProfile();
    })();
  }, []);

  const handleImageError = () => {
    setImageError(true);
  };

  if (loading) {
    return (
      <div className="h-full w-full flex items-center justify-center text-xl font-semibold py-4 px-10">
        Loading profile...
      </div>
    );
  }
  if (error || !profile) {
    return (
      <div className="h-full w-full flex items-center justify-center text-xl font-semibold py-4 px-10">
        {error || "Profile not found."}
      </div>
    );
  }

  // Use the normalized user data with proper fallbacks
  const user = profile;
  const displayInfo = getUserDisplayInfo(user);
  // Get stats with defaults
  const stats = {
    courses: {
      completed: getUserField(user, "stats.courses.completed", 0),
      total: getUserField(user, "stats.courses.total", 10),
    },
    quizzes: {
      completed: getUserField(user, "stats.quizzes.completed", 0),
      total: getUserField(user, "stats.quizzes.total", 10),
    },
    materials: {
      completed: getUserField(user, "stats.materials.completed", 0),
      total: getUserField(user, "stats.materials.total", 10),
    },
  };

  const getInitials = (name = "") => {
    if (!name) return "";
    const words = name.trim().split(" ");
    if (words.length === 1) {
      return words[0].charAt(0).toUpperCase();
    }
    return (
      words[0].charAt(0).toUpperCase() +
      words[words.length - 1].charAt(0).toUpperCase()
    );
  };

  return (
    <div className="h-full w-full relative flex-1">
      {/* Profile Card */}
      <div className="bg-white rounded-2xl px-8 py-6 flex flex-col md:flex-row items-center md:items-start justify-between gap-6 shadow-md">
        <div className="flex items-center gap-6 w-full md:w-auto">
          {displayInfo.profileImage ? (
            <img
              src={displayInfo.profileImage}
              alt="avatar"
              className="w-[90px] h-[90px] rounded-2xl object-cover border border-gray-200"
              onError={handleImageError}
            />
          ) : (
            <div className="w-[90px] h-[90px] rounded-2xl bg-[#5D93A5] flex items-center justify-center text-white text-4xl font-bold border border-gray-200">
              {getInitials(displayInfo.name)}
            </div>
          )}

          <div className="flex flex-col items-start justify-start gap-4">
            <div>
              <div className="font-bold text-2xl text-[var(--default-font)]">
                {displayInfo.name}
              </div>
              <div className="text-[var(--default-font)] text-base">
                {displayInfo.role}
              </div>
            </div>
            <div className="text-[var(--default-font)] text-sm flex items-center gap-2">
              <div className="text-[var(--primary-font)] text-sm underline cursor-pointer mb-1">
                {displayInfo.email}
              </div>
              {displayInfo.gender && (
                <>
                  Gender:{" "}
                  <span className="font-semibold">{displayInfo.gender}</span>
                  <span className="mx-2">|</span>
                </>
              )}
              {displayInfo.age && (
                <>
                  Age: <span className="font-semibold">{displayInfo.age}</span>
                </>
              )}
            </div>
          </div>
        </div>
        <button
          className="flex items-center gap-2 bg-[var(--primary-font)] text-white px-6 py-2 rounded-full hover:bg-[var(--secondary-font)] transition text-sm font-medium self-end md:self-start"
          onClick={() => setShowProfileForm(true)}
        >
          <PenIcon className="w-4 h-4" />
          Edit Profile
        </button>
      </div>

      {/* Additional Details */}
      <div className="bg-white rounded-2xl px-8 py-6 mt-6 shadow-md font-helvetica w-full">
        <div className="font-semibold text-lg text-[var(--secondary-font)] mb-4">
          Additional Details
        </div>
        <div className="w-full flex flex-col items-center justify-center gap-4">
          <div className="flex w-full items-center justify-evenly gap-4">
            <div className="w-full relative flex-1">
              <div className="text-xs text-[var(--default-font)] mb-1">
                Aadhaar Number
              </div>
              <Input
                value={displayInfo.aadhaarNumber || "Not provided"}
                readOnly
                className="w-full px-4 py-2 rounded-[0.75rem] border border-gray-200 bg-[#f7fafc] text-gray-700 text-sm"
              />
            </div>
            <div className="w-full relative flex-1">
              <div className="text-xs text-[var(--default-font)] mb-1">
                Mobile Number
              </div>
              <Input
                value={displayInfo.mobileNumber || "Not provided"}
                readOnly
                className="w-full flex-1 px-4 py-2 rounded-[0.75rem] border border-gray-200 bg-[#f7fafc] text-gray-700 text-sm"
              />
            </div>
            <div className="w-full relative flex-1">
              <div className="text-xs text-[var(--default-font)] mb-1">
                Last/Current Ship's Name
              </div>
              <Input
                value={displayInfo.ship || "Not provided"}
                readOnly
                className="w-full px-4 py-2 rounded-[0.75rem] border border-gray-200 bg-[#f7fafc] text-gray-700 text-sm"
              />
            </div>
          </div>
          <div className="flex w-full items-center justify-evenly gap-4">
            <div className="w-full relative flex-1">
              <div className="text-xs text-[var(--default-font)] mb-1">
                IMO Number
              </div>
              <Input
                value={displayInfo.imo || "Not provided"}
                readOnly
                className="w-full px-4 py-2 rounded-[0.75rem] border border-gray-200 bg-[#f7fafc] text-gray-700 text-sm"
              />
            </div>
            <div className="col-span-2 md:col-span-1 w-full relative flex-1">
              <div className="text-xs text-[var(--default-font)] mb-1">
                Seafarer's Identity Document (SID)
              </div>
              <div className="flex items-center gap-2 relative">
                <Input
                  value={
                    displayInfo.seafarerSid
                      ? displayInfo.seafarerSid.split("/").pop()
                      : "Not provided"
                  }
                  readOnly
                  className="w-full flex-1 px-4 py-2 rounded-[0.75rem] border border-gray-200 bg-[#f7fafc] text-gray-700 text-sm"
                />
                {displayInfo.seafarerSid && (
                  <Link
                    to={displayInfo.seafarerSid}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[var(--default-font)] text-xs underline cursor-pointer absolute right-4"
                  >
                    View SID Document
                  </Link>
                )}
              </div>
            </div>
            <div className="w-full relative flex-1">
              <div className="text-xs text-[var(--default-font)] mb-1">
                INDoS Number
              </div>
              <Input
                value={displayInfo.cdc || "Not provided"}
                readOnly
                className="w-full flex-1 px-4 py-2 rounded-[0.75rem] border border-gray-200 bg-[#f7fafc] text-gray-700 text-sm"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Current Stats Section - Redesigned */}
      {/* <div className="mt-6 w-full">
        <div className="font-semibold text-lg text-[var(--secondary-font)] mb-4">
          Current Stats
        </div>
        <div className="flex flex-col md:flex-row gap-6 justify-between">
          {statChartColor.map((item) => {
            const stat = stats[item.key] || { completed: 0, total: 0 };
            const percent = stat.total
              ? Math.round((stat.completed / stat.total) * 100)
              : 0;
            return (
              <div
                key={item.key}
                className="flex-1 flex flex-row items-center bg-[#f7fafc] rounded-2xl px-6 py-6 min-w-[280px] max-w-[400px] mx-auto shadow-md"
              >
                <div className="flex flex-col items-center justify-center w-1/2 gap-3">
                  <div className="text-base font-medium text-black text-center whitespace-nowrap">
                    {item.label}
                  </div>
                  <div className="flex items-center justify-center">
                    <PieChart
                      percent={percent}
                      size={70}
                      color={item.color}
                      bgColor={item.bgColor}
                    />
                  </div>
                </div>

                <div className="flex flex-col items-start justify-center w-1/2 gap-1">
                  <div className="text-4xl font-bold text-[var(--primary-font)]">
                    {percent}%
                  </div>
                  <div className="text-sm text-[var(--secondary-font)]">
                    {stat.completed}/{stat.total}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div> */}
      {/* Profile Completion Modal */}
      {showProfileForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3 sm:p-4">
          <div className="relative bg-white rounded-2xl shadow-lg w-full max-w-5xl max-h-[min(92vh,920px)] flex flex-col overflow-hidden">
            <ProfileCreationForm
              profile={profile}
              closeForm={() => setShowProfileForm(false)}
              onUpdate={refetchProfile}
            />
          </div>
        </div>
      )}
    </div>
  );
}
